"use server";

import crypto from "node:crypto";
import bcrypt from "bcryptjs";
import { cookies } from "next/headers";
import { prisma } from "@/lib/prisma";
import { sendEmail, passwordResetHtml } from "@/lib/email";
import { normalizeLang, LANG_COOKIE, t } from "@/lib/i18n";
import { clampChildren } from "@/lib/subjects/quota";

async function reqLang() {
  return normalizeLang((await cookies()).get(LANG_COOKIE)?.value);
}

export type RegisterState = { ok?: boolean; error?: string };

export async function registerAction(
  _prev: RegisterState,
  formData: FormData,
): Promise<RegisterState> {
  const email = String(formData.get("email") ?? "")
    .toLowerCase()
    .trim();
  const password = String(formData.get("password") ?? "");
  const name = String(formData.get("name") ?? "").trim();
  const lang = await reqLang();

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { error: t(lang, "errors.invalid_email") };
  }
  if (password.length < 8) {
    return { error: t(lang, "errors.password_min8") };
  }

  const existing = await prisma.user.findUnique({ where: { email } });
  if (existing) {
    return { error: t(lang, "errors.email_exists") };
  }

  await prisma.user.create({
    data: {
      email,
      name: name || null,
      passwordHash: await bcrypt.hash(password, 12),
      role: "CUSTOMER",
      childrenCount: clampChildren(formData.get("childrenCount")),
    },
  });

  return { ok: true };
}

// ===== Reset hasła =====
const RESET_PREFIX = "reset:";
const sha256 = (s: string) =>
  crypto.createHash("sha256").update(s).digest("hex");

export type ResetState = { ok?: boolean; error?: string };

// /forgot — zawsze zwraca ok (anty-enumeracja e-maili).
export async function requestResetAction(
  _prev: ResetState,
  formData: FormData,
): Promise<ResetState> {
  const email = String(formData.get("email") ?? "").toLowerCase().trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { error: t(await reqLang(), "errors.invalid_email") };
  }

  const user = await prisma.user.findUnique({ where: { email } });
  if (user) {
    const raw = crypto.randomBytes(32).toString("hex");
    const identifier = RESET_PREFIX + email;
    await prisma.verificationToken.deleteMany({ where: { identifier } });
    await prisma.verificationToken.create({
      data: {
        identifier,
        token: sha256(raw),
        expires: new Date(Date.now() + 60 * 60 * 1000), // 1h
      },
    });
    const base = process.env.NEXTAUTH_URL ?? "https://dev.fablio.me";
    await sendEmail({
      to: email,
      subject: "Reset hasła – Fablio",
      html: passwordResetHtml(`${base}/reset?token=${raw}`),
    });
  }
  return { ok: true };
}

// /reset — ustawienie nowego hasła na podstawie tokenu.
export async function resetPasswordAction(
  _prev: ResetState,
  formData: FormData,
): Promise<ResetState> {
  const token = String(formData.get("token") ?? "");
  const next = String(formData.get("next") ?? "");
  const confirm = String(formData.get("confirm") ?? "");
  const lang = await reqLang();

  if (next.length < 8) return { error: t(lang, "errors.password_min8") };
  if (next !== confirm) return { error: t(lang, "errors.passwords_mismatch") };
  if (!token) return { error: t(lang, "errors.no_token") };

  const vt = await prisma.verificationToken.findUnique({
    where: { token: sha256(token) },
  });
  if (!vt || !vt.identifier.startsWith(RESET_PREFIX) || vt.expires < new Date()) {
    return { error: t(lang, "errors.link_expired") };
  }

  const email = vt.identifier.slice(RESET_PREFIX.length);
  await prisma.user.update({
    where: { email },
    data: { passwordHash: await bcrypt.hash(next, 12) },
  });
  await prisma.verificationToken.delete({ where: { token: vt.token } });

  return { ok: true };
}
